/**
 * The account runtime: the login token, the /api client, and the mirror the
 * menu reads.
 *
 * Same shape as `NetRuntime`, `TrainingRuntime` and `ProgressionRuntime`, for
 * the same reason: `MenuModel` must stay constructible in Node with no fetch
 * and no storage, so it cannot own a session. It reads an `AccountView`
 * snapshot and raises effects; this owns the truth.
 *
 * The runtime is also what hands the server-loaded profile to
 * `ProgressionRuntime` (via `onSession`) and what supplies the WebSocket layer
 * its auth token. Signed out, both go back to nothing: an anonymous player has
 * a starter profile in memory and no way onto a server.
 */

import {
  AccountApi,
  apiBaseFor,
  loadAuthToken,
  saveAuthToken,
  type AccountSession,
  type AccountUser,
  type ApiResult,
  type ProfileOp,
  type ProfileUpdate,
} from '../game/account';
import type { GameSettings } from '../game/settings';
import { pageIsSecure } from '../net/ws-transport';

export type AccountPhase =
  /** No session. The account screen offers sign-in and registration. */
  | 'signedOut'
  /** A request is in flight. The rows lock so it cannot be double-sent. */
  | 'busy'
  /** A live session; `user` is set and the profile came from the server. */
  | 'signedIn';

/**
 * Whether the last thing that happened went well.
 *
 * The phase cannot answer this and it is the only question the player has.
 * Registering successfully and being refused for a taken email BOTH end in
 * `signedOut`, so without this the screen can only say something happened, in
 * the same colour either way.
 */
export type AccountTone = 'info' | 'error' | 'success';

export interface AccountView {
  phase: AccountPhase;
  user: AccountUser | null;
  /** One line for the account screen's subtitle: what just happened. */
  message: string;
  tone: AccountTone;
}

export interface AccountHooks {
  /** The view moved; re-read `view` and re-render. */
  changed(): void;
  /** A server-loaded session arrived (login or startup /me). */
  onSession(session: AccountSession): void;
  /** The session ended; player state must return to the anonymous default. */
  onSignedOut(): void;
  toast(text: string): void;
}

export interface AccountRuntimeOptions {
  /** Injected for tests; defaults to the real API against the settings address. */
  api?: AccountApi;
  /** Injected for tests; defaults to localStorage via browserStorage(). */
  loadToken?: () => string | null;
  saveToken?: (token: string | null) => void;
}

export class AccountRuntime {
  private readonly api: AccountApi;
  private readonly saveTokenFn: (token: string | null) => void;
  private readonly hooks: AccountHooks;

  private phase: AccountPhase = 'signedOut';
  private user: AccountUser | null = null;
  /**
   * Empty until something has actually been attempted.
   *
   * This is a STATUS, not a tagline: it is rendered as the subtitle of the
   * account screens, so a standing message here would replace the welcome
   * screen's own line with a nudge before the player has touched anything —
   * and, worse, would make "no news" and "here is what happened" look
   * identical.
   */
  private message = '';
  private tone: AccountTone = 'info';
  private authToken: string | null = null;

  constructor(settings: GameSettings, hooks: AccountHooks, options: AccountRuntimeOptions = {}) {
    this.hooks = hooks;
    this.api =
      options.api ??
      new AccountApi({
        base: () => apiBaseFor(settings.multiplayer.serverAddress, pageIsSecure()),
      });
    const load = options.loadToken ?? loadAuthToken;
    this.saveTokenFn = options.saveToken ?? saveAuthToken;
    this.authToken = load();
  }

  get view(): AccountView {
    return { phase: this.phase, user: this.user, message: this.message, tone: this.tone };
  }

  /** The bearer token, for the WebSocket Hello. Null while signed out. */
  get token(): string | null {
    return this.phase === 'signedIn' ? this.authToken : null;
  }

  /**
   * The API client, for the read-only surfaces this runtime does not own.
   *
   * The ranked screen fetches through it (see ./ranked) rather than building
   * a second `AccountApi`: the base address is derived from the settings and
   * read per call, and two clients would be two answers to "where is the
   * server" the moment the address row is edited.
   */
  get client(): AccountApi {
    return this.api;
  }

  /** The signed-in user's id, or null. For anything that highlights "you". */
  get userId(): number | null {
    return this.phase === 'signedIn' ? (this.user?.id ?? null) : null;
  }

  get signedIn(): boolean {
    return this.phase === 'signedIn';
  }

  // -------------------------------------------------------------------------
  // Session lifecycle
  // -------------------------------------------------------------------------

  /** Try to resume the stored session at startup. Quietly does nothing without
   *  a token; a dead token is dropped rather than nagged about. */
  resume(): void {
    const token = this.authToken;
    if (token === null) return;
    this.begin('Signing in…');
    void this.api.me(token).then(result => {
      if (result.ok) {
        this.adopt({ ...result.value, token });
        return;
      }
      // 401 means the session expired — forget it. Anything else (server down)
      // keeps the token so a reload after the server returns still signs in.
      if (result.status === 401) {
        this.authToken = null;
        this.saveTokenFn(null);
      }
      this.settle('signedOut', result.status === 401 ? 'Session expired — sign in again.' : result.error);
    });
  }

  login(email: string, password: string): void {
    if (this.phase === 'busy') return;
    if (email.length === 0 || password.length === 0) {
      this.settle(this.phase, 'Enter your email and password first.');
      return;
    }
    this.begin('Signing in…');
    void this.api.login(email, password).then(result => {
      if (result.ok) {
        this.adopt(result.value);
        this.hooks.toast(`Signed in as ${result.value.user.displayName}.`);
        return;
      }
      this.settle('signedOut', result.error);
    });
  }

  register(email: string, displayName: string, password: string): void {
    if (this.phase === 'busy') return;
    if (email.length === 0 || displayName.length === 0 || password.length === 0) {
      this.settle(this.phase, 'Fill in email, name and password first.');
      return;
    }
    this.begin('Creating the account…');
    void this.api.register(email, displayName, password).then(result => {
      this.settle(
        'signedOut',
        result.ok
          ? result.value.message || 'Account created. Verify your email, then sign in.'
          : result.error,
        result.ok ? 'success' : 'error'
      );
    });
  }

  resendVerification(email: string, password: string): void {
    if (this.phase === 'busy') return;
    if (email.length === 0 || password.length === 0) {
      this.settle(this.phase, 'Enter your email and password first.');
      return;
    }
    this.begin('Asking for a new link…');
    void this.api.resend(email, password).then(result => {
      this.settle('signedOut', result.ok ? result.value.message : result.error, result.ok ? 'success' : 'error');
    });
  }

  requestPasswordReset(email: string): void {
    if (this.phase === 'busy') return;
    if (email.length === 0) {
      this.settle(this.phase, 'Enter your email first.');
      return;
    }
    this.begin('Asking for a reset link…');
    void this.api.resetRequest(email).then(result => {
      this.settle('signedOut', result.ok ? result.value.message : result.error, result.ok ? 'success' : 'error');
    });
  }

  logout(): void {
    const token = this.authToken;
    this.authToken = null;
    this.saveTokenFn(null);
    this.user = null;
    this.phase = 'signedOut';
    this.message = 'Signed out.';
    this.tone = 'info';
    if (token !== null) void this.api.logout(token);
    this.hooks.onSignedOut();
    this.hooks.changed();
  }

  // -------------------------------------------------------------------------
  // Profile mutations — relayed to the server, truth adopted from the answer
  // -------------------------------------------------------------------------

  /**
   * Send one profile operation. Fire-and-forget from the caller's point of
   * view: the server's answer lands as a fresh session profile through
   * `onSession`, which overwrites whatever the client predicted.
   */
  push(op: ProfileOp): void {
    const token = this.token;
    const user = this.user;
    if (token === null || user === null) return;
    void this.api.apply(token, op).then((result: ApiResult<ProfileUpdate>) => {
      if (!result.ok) {
        // The server refused something the client predicted would work — the
        // local state is now wrong. Re-pull the truth and say what happened.
        this.hooks.toast(result.error);
        void this.api.me(token).then(me => {
          if (me.ok) this.adopt({ ...me.value, token });
        });
        return;
      }
      this.hooks.onSession({
        token,
        user,
        profile: result.value.profile,
        claims: result.value.claims,
      });
    });
  }

  // -------------------------------------------------------------------------

  private begin(message: string): void {
    this.phase = 'busy';
    this.message = message;
    this.tone = 'info';
    this.hooks.changed();
  }

  private settle(phase: AccountPhase, message: string, tone: AccountTone = 'error'): void {
    this.phase = phase;
    this.message = message;
    // `error` is the default because every unannotated settle in this class is
    // a request that did not do what was asked. Success has to say so.
    this.tone = tone;
    this.hooks.changed();
  }

  private adopt(session: AccountSession): void {
    this.authToken = session.token;
    this.saveTokenFn(session.token);
    this.user = session.user;
    this.phase = 'signedIn';
    this.message = `Signed in as ${session.user.displayName}.`;
    this.tone = 'success';
    this.hooks.onSession(session);
    this.hooks.changed();
  }
}
